import React, { useEffect, useState } from 'react'
import { Select, Checkbox } from '@arnsbogroup/react-voicearchive'
import TimeRange from 'components/TimeRange'
import { useIntl } from "react-intl";
import messages from "./messages";
import useCurrentUser from 'hooks/useCurrentUser'

const SupplierTurnaround = ({ supplierId }) => {
  const { formatMessage } = useIntl()
  const [availableForWorkDay, setAvailableForWorkDay] = useState([null, null])
  const [availableForWorkNight, setAvailableForWorkNight] = useState([null, null])
  const [turnAroundTimeOptions, setTurnAroundTimeOptions] = useState([])
  const [supplier, setSupplier] = useState(null)
  const [errors, setErrors] = useState([])

  if (!supplierId) {
    return null
  }

  const getStringTime = (value) => {
    const date = new Date(value*1000);
    return [date.getHours(), date.getMinutes()].join(':')
  }

  const { roles = [] } = useCurrentUser()

  useEffect(() => {
    fetch('/rest/v2/supplier_turnaroundtime')
      .then((response) => response.json())
      .then((turnAroundTimes) => {
        setTurnAroundTimeOptions(turnAroundTimes.map((turnAroundTime) => ({value: turnAroundTime.uid, label: turnAroundTime.name})))
      })
      .catch((err) => {
        toastr.error(err)
      })
  }, [])

  useEffect(() => {
    fetch(`/rest/v2/supplier/${supplierId}?includeRelations=1`)
      .then((response) => response.json())
      .then((data) => {
        setSupplier(data)
      })
      .catch((err) => {
          toastr.error(err)
      })
  }, [supplierId])

  useEffect(() => {
    if (supplier) {
      if ((new Date(supplier.attributeValues.dayTimeFrom.value*1000)).getTime() > 0 && (new Date(supplier.attributeValues.dayTimeTo.value*1000)).getTime() > 0) {
        setAvailableForWorkDay([getStringTime(supplier.attributeValues.dayTimeFrom.value), getStringTime(supplier.attributeValues.dayTimeTo.value)])
      }
      if ((new Date(supplier.attributeValues.nightTimeFrom.value*1000)).getTime() > 0 && (new Date(supplier.attributeValues.nightTimeTo.value*1000)).getTime() > 0) {
        setAvailableForWorkNight([getStringTime(supplier.attributeValues.nightTimeFrom.value), getStringTime(supplier.attributeValues.nightTimeTo.value)])
      }
    }
  }, [supplier])

  if (!supplier) {
    return null
  }

  const onChangeHandler = (data) => {
    if (!turnAroundTimeOptions.find((turnaroundTime) => turnaroundTime.value == data.attributeValues.turnAroundTime.value)) {
      setErrors([formatMessage(messages.validationFailed)])
    } else {
      setErrors([])
    }
    const diff = Object.keys(data).reduce((diff, key) => {
      if (supplier[key] == data[key]) return diff
      return { ...diff, [key]: data[key] }
    }, {})

    if (Object.keys(diff).length === 0 && diff.constructor === Object) {
        return
    }

    let prevState = supplier;
    setSupplier(data)
    fetch(`/rest/v2/supplier/${supplier.uid}`, {
      credentials: 'same-origin',
      method: 'PUT',
      body: JSON.stringify(diff),
    })
    .then((response) => response.json())
    .then((response) => {
        toastr.success('Updated')
    })
    .catch((error) => {
        toastr.error(error)
        setSupplier(prevState)
    })
  }

  const onAttributeValueChange = (attribute, value) => {
    onChangeHandler({
      ...supplier, 
      attributeValues: {
        ...supplier.attributeValues,
        [attribute]: {
          uid: supplier.attributeValues[attribute].uid,
          value: value
        }
      }
    })
  }

  const isCoordinator = roles.includes('coordinator')

  return (
    <div>
      <div className='mb-6'>
        <Select
          required
          label={formatMessage(messages.turnaroundTimeLabel)}
          description={formatMessage(isCoordinator ? messages.turnaroundTimeDescriptionCoordinator : messages.turnaroundTimeDescriptionSupplier)}
          options={turnAroundTimeOptions}
          value={supplier.attributeValues.turnAroundTime.value ? 
            turnAroundTimeOptions.find((turnaroundTime) => turnaroundTime.value == supplier.attributeValues.turnAroundTime.value)
            : {}}
          onChange={(turnaroundTime) => onAttributeValueChange('turnAroundTime', turnaroundTime.value)}
          errors={errors}
        />
      </div>
      <div className='space-y-6'>
        <div className='grid grid-cols-1 gap-6 md:grid-cols-2 order-3 md:order-none'>
          <TimeRange 
            disableClock
            clearIcon={null}
            description={formatMessage(isCoordinator ? messages.dayTimeDescriptionCoordinator : messages.dayTimeDescriptionSupplier)}
            label={formatMessage(messages.dayTimeLabel)}
            getTimestamp
            onChange={(value) => {
              onChangeHandler({
                ...supplier, 
                attributeValues: {
                  ...supplier.attributeValues,
                  dayTimeFrom: {
                    uid: supplier.attributeValues.dayTimeFrom.uid,
                    value: value[0]
                  },
                  dayTimeTo: {
                    uid: supplier.attributeValues.dayTimeTo.uid,
                    value: value[1]
                  }
                }
              });
            }} 
            value={availableForWorkDay}
          />
          <TimeRange 
            disableClock
            clearIcon={null}
            description={formatMessage(isCoordinator ? messages.nightTimeDescriptionCoordinator : messages.nightTimeDescriptionSupplier)}
            label={formatMessage(messages.nightTimeLabel)}
            getTimestamp
            onChange={(value) => {
              onChangeHandler({
                ...supplier, 
                attributeValues: {
                  ...supplier.attributeValues,
                  nightTimeFrom: {
                    uid: supplier.attributeValues.nightTimeFrom.uid,
                    value: value[0]
                  },
                  nightTimeTo: {
                    uid: supplier.attributeValues.nightTimeTo.uid,
                    value: value[1]
                  }
                }
              });
            }} 
            value={availableForWorkNight} 
          />
        </div>
        <div className='flex'>
          <Checkbox
            label={formatMessage(messages.weekendAvailabilityLabel)}
            checked={+ supplier.attributeValues.weekends.value}
            onChange={(e) => onAttributeValueChange('weekends', + e.currentTarget.checked)}
          />
        </div>
      </div>
    </div>
  )
}

export default SupplierTurnaround
